'use client';

import { useState, useCallback, useMemo } from 'react';

// Components
import Button from '@src/components/atoms/button';
import Filter from '@src/components/molecules/online-profile/filter';
import Search from '@src/components/molecules/online-profile/search';
import ModalSearch from '@src/components/atoms/modal-search-list-listing/modal-search-list-listing';
import ModalBantuanSearchListListing from '@src/components/atoms/modal-bantuan-search-list-listing/modal-bantuan-search-list-listing';
import ModalSaranLokasi from '@src/components/atoms/modal-saran-lokasi';
import ModalTanyaMarketing from '@src/components/atoms/modal-tanya-marketing';

// Context
import useOnlineProfileCtx from '@src/context/online-profile-ctx';

// Hooks
import { useDebounce } from '@src/hooks/useDebounce';

// Api
import { useGetListLocation } from '@src/apis/list-listing/query';

const FilterAndSearchOrganism = () => {
  const { onlineProfile, filter, setFilter, listListing: listListingData } = useOnlineProfileCtx();
  const profile = useMemo(() => onlineProfile?.data?.data, [onlineProfile]);

  const [searchModalDisplay, setSearchModalDisplay] = useState('');
  const [filterDisplay, setFilterDisplay] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [searchLocationParams, setSearchLocationParams] = useState({
    q: '',
    city_id: null,
    page: 1,
    _limit: 10,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useGetListLocation({ params: searchLocationParams });

  const listListing = useMemo(() => {
    if (!data?.pages?.length) return [];

    return data.pages.map((d) => d?.data?.data ?? []).flat();
  }, [data]);

  const [openModal, setOpenModal] = useState({
    search: false,
    help: false,
    suggestLocation: false,
    tanyaMarketing: false,
  });

  const handleOpenModal = (type) => {
    const newObj = { ...openModal };

    for (const i in newObj) {
      newObj[i] = i === type;
    }

    if (type === 'search' && !!filterDisplay) {
      setSearchModalDisplay(filterDisplay);
    }

    setOpenModal(newObj);
  };

  const handleCloseModal = () => {
    handleOpenModal();
  };

  const { debounceFn } = useDebounce({
    onChange: (newParams) => {
      setSearchLocationParams({ ...searchLocationParams, ...newParams });
      setIsTyping(false);
    },
  });

  const handleChangeSearch = (e) => {
    const newSearch = e.target.value ?? '';

    setSearchModalDisplay(newSearch);
    setIsTyping(true);
    debounceFn({ q: newSearch.trim(), page: 1 });
  };

  const lastItemRef = useCallback(
    (node) => {
      if (!node || isFetchingNextPage || !hasNextPage) return;

      setTimeout(() => {
        try {
          const rect = node.getBoundingClientRect();
          const isInViewport =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <=
              (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
              (window.innerWidth || document.documentElement.clientWidth);

          if (isInViewport && hasNextPage) {
            fetchNextPage();
          } else if (hasNextPage) {
            const observer = new IntersectionObserver(
              (entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                  fetchNextPage();
                  observer.disconnect();
                }
              },
              { threshold: 0.1, rootMargin: '100px' }
            );

            observer.observe(node);

            setTimeout(() => observer.disconnect(), 5000);
          }
        } catch (error) {
          console.error('Error in intersection detection:', error);
        }
      }, 300);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleSelectLocation = (data) => {
    const searchParams = {
      area_id: null,
      city_id: null,
      keywords: null,
    };
    if (data?.slug === 'Area') {
      searchParams.area_id = data.id;
    }

    if (data?.slug === 'City') {
      searchParams.city_id = data.id;
    }

    if (data?.slug === 'Keyword') {
      searchParams.keywords = data.name;
    }

    setFilter({ ...filter, ...searchParams });
    handleCloseModal();
    setFilterDisplay(data.name);
    setSearchModalDisplay(data.name);
  };

  const handleSelectAreaSpecialist = (city_id, name, type) => {
    const searchParams = {
      area_id: null,
      city_id: null,
      keywords: null,
    };

    if (type === 'area') {
      searchParams.area_id = city_id;
    } else if (type === 'city') {
      searchParams.city_id = city_id;
    }

    setFilter({ ...filter, ...searchParams });
    setFilterDisplay(name);
    setSearchModalDisplay(name);
  };

  const handleClearSearch = () => {
    const searchParams = {
      area_id: null,
      city_id: null,
      keywords: null,
    };

    setFilter({ ...filter, ...searchParams });
    setFilterDisplay("");
    setSearchModalDisplay("");
  };

  const listArea = useMemo(() => {
    if (!profile?.list_area?.length) return [];

    return profile.list_area.filter((area) => !area.is_area_specialist);
  }, [profile]);

  const disabledArea = useMemo(() => {
    if (!listListingData?.pages?.length) return true;

    return !listListingData.pages.flatMap((d) => d?.data?.data ?? []).length;
  }, [listListingData]);

  return (
    <>
      <div
        className="border-b border-solid border-[#DEDFDE] mb-6 pb-2 flex items-center justify-between gap-2 
        dd2-xl:border-b dd2-xl:border-solid dd2-xl:border-[#DEDFDE]
        dd2-lg:flex-col dd2-xl:border-none dd2-lg:border-[unset]"
      >
        {/* Filter Mobile */}
        <div className="mb-2 pb-2 border-b border-solid border-[#DEDFDE] w-full hidden items-center gap-2 dd2-lg:flex">
          <Search
            value={filterDisplay}
            placeholder="Masukkan area"
            onClick={() => handleOpenModal('search')}
            onClear={handleClearSearch}
            inputClassName="dd2-xl:hidden dd2-lg:block"
          />
          <Filter />
        </div>

        <div className={`flex items-end w-[calc(100%-305px)] dd2-xl:w-[calc(100%-138px)] dd2-lg:w-full ${disabledArea ? '!bg-[#F5F5F5] w-fit' : ''}`}>
          <Button
            onClick={handleClearSearch}
            disabled={disabledArea}
            className={`${
              !filter.city_id && !filter.area_id
                ? '!text-[#fff] !bg-[#252526]'
                : '!text-[#252526] !bg-[#fff]'
            } ${disabledArea ? 'disabled:cursor-not-allowed disabled:!bg-[#DEDFDE] disabled:!text-[#fff]' : 'cursor-pointer'} mr-4 !rounded-[24px]`}
          >
            Semua
          </Button>

          <div className="w-[calc(100%-70px)] dd2-lg:w-full relative">
            <div
              className={`overflow-x-auto`}
            >
              <div className="flex items-center">
                {!!listArea.length && (
                  <div className="relative py-1 px-[12px] flex items-center gap-[12px] rounded-[8px]">
                    {listArea.map((area, idx) => (
                      <button
                        onClick={() => handleSelectAreaSpecialist(area.city_id, area.name, "city")}
                        disabled={disabledArea}
                        className={`${
                          filter.city_id === area.city_id
                            ? '!text-[#fff] !bg-[#252526] px-4 rounded-[24px]'
                            : '!text-[#686A68]'
                        } font-satoshi-bold h-[36px] flex items-center whitespace-nowrap ${disabledArea ? 'disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:!text-[#DEDFDE]' : 'cursor-pointer'}`}
                        key={`list-area-${area.city_id}-${idx}`}
                      >
                        {area.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="w-[60px] h-full absolute right-0 top-0 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(250, 250, 250, 0.00) 0%, #FAFAFA 87.5%)' }} />
          </div>
        </div>

        {/* Filter Desktop */}
        <div className="flex items-center gap-2 dd2-lg:hidden">
          <Search
            placeholder="Masukkan area"
            onClick={() => handleOpenModal('search')}
            value={filterDisplay}
            onClear={handleClearSearch}
            inputClassName="dd2-xl:hidden dd2-lg:block"
          />
          <Filter />
        </div>
      </div>

      <ModalSearch
        isOpen={openModal.search}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal('help')}
        onChange={handleChangeSearch}
        value={searchModalDisplay}
        lastItemRef={lastItemRef}
        data={listListing}
        onSelect={handleSelectLocation}
        isFetching={isFetching || isFetchingNextPage || isTyping}
      />
      <ModalBantuanSearchListListing
        isOpen={openModal.help}
        onClose={handleCloseModal}
        onSuggestLocation={() => handleOpenModal('suggestLocation')}
        onTanyaMarketing={() => handleOpenModal('tanyaMarketing')}
      />
      <ModalSaranLokasi
        isOpen={openModal.suggestLocation}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal('help')}
      />
      <ModalTanyaMarketing
        isOpen={openModal.tanyaMarketing}
        onClose={handleCloseModal}
        onHelp={() => handleOpenModal('help')}
      />
    </>
  );
};

export default FilterAndSearchOrganism;
